import { roundZloty } from '@/lib/money'

/**
 * Shared vocabulary for tiered offers (montaż, grafika, …).
 *
 * Lives apart from `pricing.ts` because that file is the price source, while
 * this one is about how tiers are compared and counted. Both /pl/montaz and
 * /pl/grafika build on it, so a fix to the Polish plural rules lands in both.
 */

/** `true`/`false` render as a tick/dash; a string renders verbatim. */
export type FeatureValue = boolean | string

/** One comparison row. Keyed by tier NAME so reordering tiers can't shift columns. */
export type ComparisonRow = {
  label: string
  values: Record<string, FeatureValue>
}

/**
 * Rows ordered for a tier's card: what it includes first, what it does not
 * after. Within each group the matrix order is kept, so tiers still read down
 * in the same sequence and only the dividing line moves as you step up.
 */
export function sortRowsForTier(rows: ComparisonRow[], tier: string): ComparisonRow[] {
  return [
    ...rows.filter((row) => row.values[tier] !== false),
    ...rows.filter((row) => row.values[tier] === false),
  ]
}

/** Polish plural: 1 rolka, 2–4 rolki, 5+ rolek (with the usual teens exception). */
export function pluralize(n: number, forms: [string, string, string]): string {
  if (n === 1) return forms[0]
  const tens = n % 100
  const ones = n % 10
  if (tens >= 12 && tens <= 14) return forms[2]
  return ones >= 2 && ones <= 4 ? forms[1] : forms[2]
}

export function formatPLN(value: number): string {
  return `${value.toLocaleString('pl-PL')} zł`
}

/** Polish VAT on creative services. Quoted prices across the site are net. */
export const VAT_RATE = 0.23

export function withVat(net: number): number {
  return roundZloty(net * (1 + VAT_RATE))
}
